import { Box, Button, Icon, Modal } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';

import { FinancingDataPendingPaymentResponse } from 'types';

import { CancelPendingPaymentModal, PendingPaymentDetails } from './modals/CancelPendingPaymentModal';

interface ScheduledPaymentExistsModalProps {
  isOpen: boolean;
  onClose: () => void;
  scheduledPayment?: FinancingDataPendingPaymentResponse;
  title?: string;
}

export function ScheduledPaymentExistsModal({
  isOpen,
  onClose,
  scheduledPayment,
  title = 'Make One-Time Payment',
}: ScheduledPaymentExistsModalProps) {
  const [cancelTarget, setCancelTarget] = useState<PendingPaymentDetails | null>(null);

  const openCancel = useCallback(() => {
    if (!scheduledPayment) return;
    setCancelTarget(scheduledPayment);
  }, [scheduledPayment]);
  const closeCancel = useCallback(() => setCancelTarget(null), []);
  const handleCancelled = useCallback(() => {
    setCancelTarget(null);
    onClose();
  }, [onClose]);

  return (
    <>
      <Modal ariaLabelledBy="scheduled-payment-modal-heading" isOpen={isOpen} onDismiss={onClose}>
        <Box
          padding="lg"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          borderColor="separator"
          borderWidth="0 0 xs 0"
          style={{ flexShrink: 0 }}
        >
          <Box as="h4" id="scheduled-payment-modal-heading" fontSize={{ base: 'md', tablet: 'lg' }}>
            {title}
          </Box>
          <button
            aria-label="close"
            type="button"
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: 'var(--size-spacing-xs)',
              color: 'var(--color-text-body-primary)',
              lineHeight: 'var(--size-line-height-base)',
              borderRadius: 'var(--size-border-radius-sm)',
            }}
          >
            <Icon name="remove-light" />
          </button>
        </Box>
        <Modal.Body background="grey-50">
          <Box direction="column" childGap="md">
            <Box
              background="warning-100"
              borderColor="warning-300"
              borderWidth="xs"
              radius="md"
              padding="md"
              fontWeight="bold"
              color="warning-700"
            >
              A one-time payment is already scheduled
            </Box>
            {scheduledPayment && (
              <Box
                background="white"
                borderColor="separator"
                borderWidth="xs"
                radius="md"
                padding="md"
                direction="column"
                childGap="sm"
                fontSize="sm"
              >
                <Box direction="row" justifyContent="space-between">
                  <Box color="grey-500">Amount</Box>
                  <Box fontWeight="bold">${scheduledPayment.amount.toFixed(2)}</Box>
                </Box>
                <Box direction="row" justifyContent="space-between">
                  <Box color="grey-500">Scheduled</Box>
                  <Box>{DateTime.fromISO(scheduledPayment.paymentDate).toLocaleString(DateTime.DATE_MED)}</Box>
                </Box>
                <Box direction="row" justifyContent="space-between">
                  <Box color="grey-500">Type</Box>
                  <Box>{scheduledPayment.paymentTypeDescription}</Box>
                </Box>
                <Box direction="row" justifyContent="space-between">
                  <Box color="grey-500">Entered on</Box>
                  <Box>{DateTime.fromISO(scheduledPayment.entryDate).toLocaleString(DateTime.DATE_MED)}</Box>
                </Box>
              </Box>
            )}
          </Box>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" tone="neutral" onClick={onClose}>
            Close
          </Button>
          {scheduledPayment && (
            <Button variant="primary" tone="neutral" size="md" type="button" onClick={openCancel}>
              Cancel Payment
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      {cancelTarget && (
        <CancelPendingPaymentModal
          isOpen
          onClose={closeCancel}
          onCancelled={handleCancelled}
          payment={cancelTarget}
        />
      )}
    </>
  );
}
